/**
 * useAddStayToGroup — «Afegir una habitació al grup» des de la fitxa (runbook 7, F5 sobre B3).
 *
 * `POST /reservations/:id/stays` (només ADMIN) amb **una** estada, en el mateix format que una de
 * `POST /reservations/group`. El backend la crea a Beds24 primer: si falla (502), la unitat és
 * ocupada (409) o la reserva no ho admet (400), no desa res i el missatge surt en un toast; el full
 * es queda obert amb les dades. El backend té `forbidNonWhitelisted`: no s'envia res més.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '@/services/api'
import {
  createReservationErrorMessage,
  readCreationWarnings,
  toastCreationSuccess,
} from '@/hooks/useCreateReservation'
import type { StayLineValues } from '@/lib/schemas/create-reservation'

/** Cos de `POST /reservations/:id/stays`: exactament els camps d'una estada del DTO del grup. */
export interface AddStayPayload {
  unit_id: string
  data_entrada: string
  data_sortida: string
  num_adults: number
  num_nens?: number
  preu_total: number
  notes_internes?: string
}

export interface AddStayVariables {
  /** Id intern (cuid) de la reserva de partida (mestra o filla). */
  reservationId: string
  payload: AddStayPayload
}

/** El que fa servir el frontend de la resposta 201. */
export interface AddedStay {
  /** Número de Beds24 de la reserva nova (`reservation.beds24_id`), si la resposta el porta. */
  newBeds24Id: string | null
  /** Tots els números del grup, la mestra primer i la nova inclosa. */
  beds24Ids: string[]
  /** Ruta del grup que torna el backend (com a màxim 6 ids). */
  groupPath: string | null
  /** L'habitació és creada, però falta alguna cosa (p. ex. el client de facturació de la fitxa). */
  warnings: string[]
}

/** Valors ja validats de la línia → cos del POST. Cap camp de més; les notes buides no s'envien. */
export function buildAddStayPayload(stay: StayLineValues, notesInternes?: string): AddStayPayload {
  const notes = notesInternes?.trim()
  const payload: AddStayPayload = {
    unit_id: stay.unit_id,
    data_entrada: stay.data_entrada,
    data_sortida: stay.data_sortida,
    num_adults: stay.num_adults,
    num_nens: Number.isFinite(stay.num_nens) ? stay.num_nens : 0,
    preu_total: stay.preu_total,
  }
  if (notes) payload.notes_internes = notes
  return payload
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== ''
}

/** Llegeix de forma defensiva la resposta 201 de B3. */
export function readAddedStay(data: unknown): AddedStay {
  if (typeof data !== 'object' || data === null) {
    return { newBeds24Id: null, beds24Ids: [], groupPath: null, warnings: [] }
  }
  const record = data as Record<string, unknown>
  const ids = Array.isArray(record.beds24_ids) ? record.beds24_ids.filter(isNonEmptyString).map((id) => id.trim()) : []
  const reservation = record.reservation
  let newBeds24Id: string | null = null
  if (typeof reservation === 'object' && reservation !== null) {
    const raw = (reservation as Record<string, unknown>).beds24_id
    if (isNonEmptyString(raw)) newBeds24Id = raw.trim()
  }
  const groupPath = isNonEmptyString(record.group_path) ? record.group_path : null
  return { newBeds24Id, beds24Ids: ids, groupPath, warnings: readCreationWarnings(data) }
}

/** La crida en si, fora del hook perquè es pugui provar amb `api` mockejat. */
export async function postAddStay({ reservationId, payload }: AddStayVariables): Promise<AddedStay> {
  const response = await api.post<unknown>(`/reservations/${encodeURIComponent(reservationId)}/stays`, payload)
  return readAddedStay(response.data)
}

export interface UseAddStayToGroupOptions {
  onSuccess?: (added: AddedStay) => void
}

export function useAddStayToGroup(options: UseAddStayToGroupOptions = {}) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: postAddStay,
    onSuccess: (added, { reservationId }) => {
      toastCreationSuccess('Habitació afegida al grup', added.warnings)
      queryClient.invalidateQueries({ queryKey: ['calendar-bookings'], exact: false })
      queryClient.invalidateQueries({ queryKey: ['reservation-full', reservationId] })
      queryClient.invalidateQueries({ queryKey: ['reservation-metadata', reservationId] })
      // El grup de totes les germanes ha canviat (`group_beds24_ids`).
      queryClient.invalidateQueries({ queryKey: ['reservation-by-beds24'], exact: false })
      options.onSuccess?.(added)
    },
    onError: (error: unknown) => {
      toast.error(createReservationErrorMessage(error))
    },
  })
}

export default useAddStayToGroup
